import { FilePlus2, FileX2, Loader2, PencilLine } from 'lucide-react'
import type { ContextAuditAction, WorkspaceContextAuditEvent } from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * Reverse-chronological audit timeline for ``WorkspaceContext`` mutations.
 *
 * Each row shows the action (create / update / delete), the actor, the
 * before / after char count + sha-256 hash prefix, and the timestamp. We
 * never render the actual previous content — the backend deliberately
 * stores only hashes + char counts, so neither the UI nor the wire format
 * can replay old prompts back to a reader.
 */
export interface ContextAuditTimelineProps {
  events: WorkspaceContextAuditEvent[]
  isLoading: boolean
  error: unknown
  /** Optional override for the root data-testid. */
  testid?: string
}

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

const ACTION_TONE: Record<ContextAuditAction, 'primary' | 'info' | 'danger'> = {
  create: 'primary',
  update: 'info',
  delete: 'danger',
}

const ACTION_LABEL: Record<ContextAuditAction, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
}

const ACTION_ICON: Record<ContextAuditAction, React.ComponentType<{ className?: string }>> = {
  create: FilePlus2,
  update: PencilLine,
  delete: FileX2,
}

export function ContextAuditTimeline({
  events,
  isLoading,
  error,
  testid = 'context-audit-timeline',
}: ContextAuditTimelineProps) {
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-6"
        data-testid={`${testid}-loading`}
      >
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (error) {
    return (
      <p
        className="text-xs text-destructive"
        role="alert"
        data-testid={`${testid}-error`}
      >
        {error instanceof Error ? error.message : 'Failed to load audit log.'}
      </p>
    )
  }
  if (events.length === 0) {
    return (
      <p
        className="text-xs leading-relaxed text-muted-foreground"
        data-testid={`${testid}-empty`}
      >
        No saved context changes yet. The audit log will populate the first
        time you save or clear context.
      </p>
    )
  }
  return (
    <ol
      className="max-h-72 space-y-1 overflow-y-auto"
      data-testid={testid}
      aria-label="Saved context audit log"
    >
      {events.map((e) => (
        <li key={e.uuid}>
          <Row event={e} />
        </li>
      ))}
    </ol>
  )
}

function Row({ event }: { event: WorkspaceContextAuditEvent }) {
  const Icon = ACTION_ICON[event.action]
  const delta = formatDelta(event)
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-border bg-card px-2.5 py-1.5"
      data-testid={`audit-event-${event.uuid}`}
      data-action={event.action}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full',
          event.action === 'delete'
            ? 'bg-destructive-soft text-destructive-soft-foreground'
            : 'bg-primary-soft text-primary-soft-foreground',
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge tone={ACTION_TONE[event.action]} size="xs">
            {ACTION_LABEL[event.action]}
          </Badge>
          <span className="text-muted-foreground">{delta}</span>
          <time
            dateTime={event.created_at}
            className="ml-auto text-[10px] text-muted-foreground"
          >
            {formatTime(event.created_at)}
          </time>
        </div>
        <p className="text-[10px] font-mono text-muted-foreground/80">
          {event.previous_hash ? hashPrefix(event.previous_hash) : '—'} →{' '}
          {event.current_hash ? hashPrefix(event.current_hash) : '—'}
        </p>
      </div>
    </div>
  )
}

function hashPrefix(hash: string): string {
  return hash.slice(0, 8)
}

function formatTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return DATE_FORMAT.format(d)
}

function formatDelta(event: WorkspaceContextAuditEvent): string {
  const prev = event.previous_chars
  const curr = event.current_chars
  if (event.action === 'create' && curr != null) {
    return `0 → ${curr.toLocaleString()} chars`
  }
  if (event.action === 'delete' && prev != null) {
    return `${prev.toLocaleString()} → 0 chars`
  }
  if (event.action === 'update' && prev != null && curr != null) {
    const diff = curr - prev
    const sign = diff > 0 ? '+' : ''
    return `${prev.toLocaleString()} → ${curr.toLocaleString()} chars (${sign}${diff.toLocaleString()})`
  }
  return ''
}
